import React, { FC, useState, useMemo, useCallback } from "react";
import { Box, Button, Snackbar, Text } from "common";
import { CarrefourLogo } from '@mafc/icon';
import { useApiInfo } from "hooks";
import { AUTH_GET_USER } from "store/store.types";
import { EStores } from "store/reducers/auth.reducers";
import { SelectCountry, Badge } from "custom";
import Section from "Layout/Section";
import { useStyles } from "./Login.style";
import { useDispatch } from "react-redux";
import { clearApi } from "store/actions/api.actions";
import useAuthenticate from '../../hooks/useAuthenticate'

interface IProps {
  onCountrySelection: (storeId: string) => void;
  action: EAction;
  stores?: string[];
  role: string;
  name?: string;
}

export enum EAction {
  "Auth",
  "CountrySelect",
}

const Login: FC<IProps> = ({
  onCountrySelection,
  action,
  role,
  name = "",
}) => {
  const {
    dropDownWrapper,
    carrefourWrapperLogo,
    container,
    wrapper,
    boxWrapper,
    loginText,
  } = useStyles();

  const dispatch = useDispatch();

  const [storeId, setStoreId] = useState("");

  const { error: getUserError } =
    useApiInfo(AUTH_GET_USER);

  const onCloseError = () => {
    dispatch(clearApi(AUTH_GET_USER));
  };

  const { login, inProgress } = useAuthenticate();

  const onCountrySelectionNextHandler = useCallback(() => {
    if (storeId) {
      onCountrySelection(storeId as EStores);
    }
  }, [onCountrySelection, storeId]);

  const onChangeCountry = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStoreId(event.target.value);
  };

  const FieldsSet = useMemo(() => {
    if (action === EAction.CountrySelect) {
      return (
        <div>
          <div className={carrefourWrapperLogo}>
            <CarrefourLogo size="CUSTOM" fill="#10469b" />
          </div>
          <Badge as={role} width="auto" height="22px" />
          <Text fontSize="16px" marginY="15px" fontWeight={600}>
            Welcome {name}
          </Text>
          <Box className={dropDownWrapper}>
            <SelectCountry
              onChangeCountry={onChangeCountry}
              label="Please Select Country"
              isTransparent={false}
              value={storeId}
              disableUnderLine={false}
              size="medium"
              width="100%"
              inputStartPadding="8px"
            />
          </Box>
          <Text color="text.secondary" fontSize="10px" fontWeight={500}>
            Country code promotions are restricted to 1 country
          </Text>
          <Box display="grid">
            <Button
              data-testid="country-selection-button"
              variant="outlined"
              round
              margin="15px 0"
              width="100%"
              onClick={onCountrySelectionNextHandler}
              color="primary"
            >
              Continue
            </Button>
          </Box>
        </div>
      );
    }
    return (
      <>
        <div className={carrefourWrapperLogo}>
          <CarrefourLogo size="CUSTOM" fill="#10469b" />
        </div>
        <Text fontSize="18px" fontWeight={700} marginTop="15px" className={loginText}>
          Login to Dexter-Promotion Manager
        </Text>
        <Box gridTemplateColumns="1fr" gridGap="20px" marginY="20px">
          <Button
            variant="contained"
            round
            width="100%"
            color="primary"
            onClick={() => login()}
          >
            Login with Company SSO
          </Button>
        </Box>
      </>
    );
  }, [
    action,
    name,
    onCountrySelectionNextHandler,
    role,
    dropDownWrapper,
    storeId,
    carrefourWrapperLogo,
  ]);

  return (
    <Section height="100vh" margin="0px" width="100%" maxWidth="100%" className={container}>
      {!inProgress && <div className={wrapper} data-testid="login-box-wrapper">
        <div className={boxWrapper}>
          {(getUserError) && (
            <Snackbar
              variant="standard"
              severity="error"
              vertical="top"
              horizontal="center"
              open={!!getUserError}
              handleClose={onCloseError}
            >
              {getUserError}
            </Snackbar>
          )}
          {FieldsSet}
        </div>
      </div>
      }
    </Section>
  );
};

export default Login;
